import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges
} from "@angular/core";
import { faCircle, faPhone } from "@fortawesome/pro-solid-svg-icons";
import { DatePipe, NgClass, NgIf, NgOptimizedImage, NgFor } from "@angular/common";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { AuthGuard } from "../../auth/auth.guard";
import { ClaimItReferral } from "../../models/claim-it-referral.model";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons/faCircleCheck";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { faCircle as faCircleRegular } from "@fortawesome/pro-regular-svg-icons/faCircle";
import { faCircleX } from "@fortawesome/pro-solid-svg-icons/faCircleX";
import { UtilityService } from "../../services/utility.service";
import { environment } from "../../../environments/environment";
import { OpportunityService } from "../../services/opportunity.service";
import { Environment } from "../../../environments/environment.model";

@Component({
  selector: "app-details-panel",
  standalone: true,
  imports: [DatePipe, FaIconComponent, NgClass, NgOptimizedImage, NgIf, NgFor],
  templateUrl: "./details-panel.component.html"
})
export class DetailsPanelComponent implements OnChanges {
  @Input()
  selectedOpportunityId: number | null = null;
  protected readonly faPhone: IconDefinition = faPhone;
  protected readonly environment: Environment = environment;
  protected readonly faXmark: IconDefinition = faXmark;
  protected readonly UtilityService: typeof UtilityService = UtilityService;
  protected readonly CRITICAL_MINUTES_THRESHOLD = 5;
  protected readonly MINIMUM_MINUTES_DISPLAY = 1;
  protected selectedReferral: ClaimItReferral | null = null;
  private isFirstClick: boolean = false;
  protected icons: { [id: string]: IconDefinition } = {
    Assigned: faCircleCheck,
    Claimed: faCircleCheck,
    Available: faCircle,
    Settled: faCircleCheck,
    Incoming: faCircleRegular,
    Ineligible: faCircleX,
    Delivered: faCircleCheck,
    Undefined: faCircleX
  };
  protected iconColor: { [id: string]: string } = {
    Assigned: "text-slate-500",
    Claimed: "text-slate-500",
    Available: "text-agentium_blue-primary",
    Settled: "text-slate-500",
    Incoming: "text-slate-500",
    Ineligible: "text-slate-500",
    Delivered: "text-slate-500"
  };
  protected statusDisplay: { [id: string]: string } = {
    Assigned: "Assigned",
    Claimed: "Claimed",
    Available: "Available",
    Settled: "Settled",
    Incoming: "Incoming",
    Ineligible: "Ineligible",
    Delivered: "Delivered"
  };

  constructor(
    protected authGuard: AuthGuard,
    protected opportunityService: OpportunityService,
    private elementRef: ElementRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["selectedReferral"]) {
      if (changes["selectedReferral"].currentValue != null) {
        this.isFirstClick = true;
      }
    }
    if (this.selectedOpportunityId != null) {
      this.opportunityService
        .getReferral(this.selectedOpportunityId)
        .subscribe((referral: ClaimItReferral): void => {
          this.selectedReferral = referral;
        });
    }
  }

  clearReferralSelection(): void {
    this.selectedReferral = null;
  }

  @HostListener("document:click", ["$event"])
  onClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target) && !this.isFirstClick) {
      this.clearReferralSelection();
    }
    this.isFirstClick = false;
  }

  getMinutesLeftLabel(minutesLeft: number): string {
    const displayMinutes = Math.max(minutesLeft, this.MINIMUM_MINUTES_DISPLAY);
    const plural = displayMinutes === 1 ? "" : "s";
    return `${displayMinutes} minute${plural} left`;
  }

  getMinutesLeftClass(minutesLeft: number): string {
    return minutesLeft <= this.CRITICAL_MINUTES_THRESHOLD ? "text-red-600" : "text-slate-600";
  }

  hasPriceDecay(): boolean {
    const referral = this.selectedReferral;
    return (
      referral != null &&
      referral.discountPrice != null &&
      referral.dynamicPrice != null &&
      referral.discountPrice > referral.dynamicPrice
    );
  }

  getMinutesLeft(marketplaceExpirationDate: string | Date): number {
    const now = new Date();
    const expiration = new Date(marketplaceExpirationDate);
    const diffMs = expiration.getTime() - now.getTime();
    const diffMinutes = Math.ceil(diffMs / 60000); // 60,000 ms in 1 minute
    return Math.max(this.MINIMUM_MINUTES_DISPLAY, diffMinutes);
  }
}
